// js/components/CosCodeReducer.js
import Delegate from 'ftdomdelegate';

class CosCodeReducer extends HTMLElement {
  constructor() {
    super();
    
    this.delegateElement = new Delegate(this);
    this.settings = JSON.parse(this.getAttribute("data-settings")) || {};
    this.isLoading = false;
  }

  connectedCallback() {
    this._attachListeners();
  }

  disconnectedCallback() {
    this.delegateElement.off();
  }

  _attachListeners() {
    // Event listener pentru butonul de aplicare din formular
    this.delegateElement.on('submit', 'form', this._onSubmit.bind(this));
    this.delegateElement.on('click', 'button[type="submit"]', this._onSubmit.bind(this));
    
    // Event listener pentru butonul cu data-action="apply-cod-reducere"
    this.delegateElement.on('click', '[data-action="apply-cod-reducere"]', this._onApplyCodeClick.bind(this));
    
    // Event listener pentru butonul de eliminare cod
    this.delegateElement.on('click', '[data-action="remove-cod-reducere"]', this._onRemoveCodeClick.bind(this));
    
    // Event listener pentru input-ul de cod
    this.delegateElement.on('input', 'input[data-check-cod-reducere]', this._onInputChange.bind(this));
  }

  _onApplyCodeClick(event, target) {
    event.preventDefault();
    const code = target.getAttribute('data-cod');
    if (code) {
      this._applyDiscountCode(code);
    }
  }

  _onRemoveCodeClick(event, target) {
    event.preventDefault();
    const code = target.getAttribute('data-cod');
    if (code) {
      this._removeDiscountCode(code);
    }
  }

  _onSubmit(event, target) {
    event.preventDefault();
    const input = this.querySelector('input[data-check-cod-reducere]');
    const code = input ? input.value.trim() : '';
    
    if (!code) {
      this._showMessage(this.settings.empty_message || 'Vă rugăm să introduceți un cod de reducere.', 'error');
      return;
    }

    this._applyDiscountCode(code);
  }

  _onInputChange(event, target) {
    // Curățăm mesajele de eroare când utilizatorul începe să scrie
    this._clearMessages();
  }

  _applyDiscountCode(code) {
    if (this.isLoading) return;
    
    this.isLoading = true;
    this._showLoadingState();

    // Aplicăm discount-ul folosind endpoint-ul nativ Shopify
    // Facem un fetch către /discount/{code} pentru a aplica codul
    fetch(`${window.Shopify.routes.root}discount/${encodeURIComponent(code)}`, {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      redirect: 'manual' // Nu urmărim redirecționările automat
    })
    .then(response => {
      // Shopify va redirecționa către cart dacă codul este valid
      if (response.type === 'opaqueredirect' || response.status === 302 || response.redirected) {
        // Codul a fost aplicat cu succes
        this._showMessage(window.theme?.settings?.cart_discount_success_message || 'Codul de reducere a fost aplicat cu succes!', 'success');
        
        // Obținem starea nouă a cart-ului
        return fetch(`${window.Shopify.routes.root}cart.js`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin'
        });
      } else {
        throw new Error(window.theme?.settings?.cart_discount_error_message || 'Codul de reducere nu este valid.');
      }
    })
    .then(response => response.json())
    .then(cartData => {
      // Emitem un event pentru a actualiza cart-ul în interfață
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));
      
      // Reîmprospătăm componenta pentru a afișa butonul de eliminare
      setTimeout(() => {
        this._refreshComponent();
      }, 1000);
    })
    .catch(error => {
      console.error('Error applying discount code:', error);
      this._showMessage(error.message || 'A apărut o eroare la aplicarea codului.', 'error');
    })
    .finally(() => {
      this.isLoading = false;
      this._hideLoadingState();
    });
  }

  _removeDiscountCode(code) {
    if (this.isLoading) return;
    
    this.isLoading = true;
    this._showLoadingState();

    // Eliminăm discount-ul folosind endpoint-ul Shopify pentru clearing
    fetch(`${window.Shopify.routes.root}checkout?discount=CLEAR`, {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    })
    .then(response => {
      if (response.ok || response.redirected) {
        // Actualizăm cart-ul pentru a obține starea nouă
        return fetch(`${window.Shopify.routes.root}cart.js`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin'
        });
      } else {
        throw new Error('Nu s-a putut elimina codul de reducere.');
      }
    })
    .then(response => response.json())
    .then(cartData => {
      // Discount-ul a fost eliminat cu succes - afișăm mesajul aici
      this._showMessage(window.theme?.settings?.cart_discount_success_message || 'Codul de reducere a fost eliminat cu succes!', 'success');
      
      // Emitem un event pentru a actualiza cart-ul în interfață
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));
      
      // Reîmprospătăm componenta pentru a afișa butonul de sugestie
      setTimeout(() => {
        this._refreshComponent();
      }, 1000);
    })
    .catch(error => {
      console.error('Error removing discount code:', error);
      this._showMessage(error.message || 'A apărut o eroare la eliminarea codului.', 'error');
    })
    .finally(() => {
      this.isLoading = false;
      this._hideLoadingState();
    });
  }

  _showLoadingState() {
    const submitButton = this.querySelector('button[type="submit"]');
    const input = this.querySelector('input[data-check-cod-reducere]');
    
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = this.settings.loading_text || 'Se aplică...';
    }
    
    if (input) {
      input.disabled = true;
    }
  }

  _hideLoadingState() {
    const submitButton = this.querySelector('button[type="submit"]');
    const input = this.querySelector('input[data-check-cod-reducere]');
    
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.textContent = this.settings.apply_text || 'Aplică';
    }
    
    if (input) {
      input.disabled = false;
    }
  }

  _showMessage(message, type = 'info') {
    this._clearMessages();
    
    const messageElement = document.createElement('div');
    messageElement.className = `cod-reducer-message cod-reducer-message--${type} f14 fw500 mt-sm`;
    messageElement.textContent = message;
    
    // Adăugăm mesajul după input wrapper
    const inputWrapper = this.querySelector('.form__input-wrapper');
    if (inputWrapper) {
      inputWrapper.insertAdjacentElement('afterend', messageElement);
    }
  }

  _clearMessages() {
    const messages = this.querySelectorAll('.cod-reducer-message');
    messages.forEach(message => message.remove());
  }

  _refreshComponent() {
    // Reîmprospătăm componenta prin reîncărcarea părții de mini-cart
    // Aceasta va actualiza butoanele în funcție de starea cart-ului
    const miniCart = document.querySelector('cos-mini-cart');
    if (miniCart && typeof miniCart.refresh === 'function') {
      miniCart.refresh();
    } else {
      // Alternativ, actualizăm manual interfața
      this._updateDiscountButtons();
    }
  }

  _updateDiscountButtons() {
    // Obținem starea curentă a cart-ului pentru a actualiza butoanele
    fetch(`${window.Shopify.routes.root}cart.js`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    })
    .then(response => response.json())
    .then(cartData => {
      const discountInfo = this.querySelector('.mini-cart__cod-reducere-info');
      if (!discountInfo) return;

      // Verificăm dacă există discount-uri aplicate
      const hasDiscounts = cartData.cart_level_discount_applications && 
                          cartData.cart_level_discount_applications.length > 0;

      // Actualizăm conținutul secțiunii
      if (hasDiscounts) {
        // Afișăm butoanele de eliminare
        let buttonsHTML = '';
        cartData.cart_level_discount_applications.forEach(discount => {
          buttonsHTML += `
            <button type="button" class="button button--secondary button--sm" data-action="remove-cod-reducere" data-cod="${discount.title}">
              ${discount.title}
              <svg class="icon icon--close" viewBox="0 0 24 24" role="presentation">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
              </svg>
            </button>
          `;
        });
        discountInfo.innerHTML = buttonsHTML;
      } else {
        // Afișăm butonul de sugestie dacă este configurat
        // Verificăm în settings din Shopify (window.theme.settings)
        const suggestionCode = window.theme?.settings?.cart_discount_suggestion_code;
        const showSuggestion = window.theme?.settings?.cart_show_discount_suggestion;
        const suggestionText = window.theme?.settings?.cart_discount_suggestion_text;
        
        if (suggestionCode && showSuggestion) {
          const displayText = suggestionText || `Aplică codul ${suggestionCode}`;
          discountInfo.innerHTML = `
            <button type="button" class="button button--secondary button--sm" data-action="apply-cod-reducere" data-cod="${suggestionCode}">
              <span>${displayText}</span>
            </button>
          `;
        } else {
          discountInfo.innerHTML = '';
        }
      }
    })
    .catch(error => {
      console.error('Error updating discount buttons:', error);
    });
  }
}

if (!customElements.get('cos-code-reducer')) {
  customElements.define('cos-code-reducer', CosCodeReducer);
}

export { CosCodeReducer };
